import React, { useEffect, useState } from "react";
import { TbFileDownload } from "react-icons/tb";
import { PiClipboardText, PiDownload } from "react-icons/pi";
import { MdOutlinePayment } from "react-icons/md";

import { FaRegFileAlt } from "react-icons/fa";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronDown, MoreHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useDispatch } from "react-redux";
import { getMyBookings } from "../actions/flightAction";
import { getMyTourBookings } from "../actions/tour/tourAction";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { FiInbox } from "react-icons/fi";
import { isEmpty } from "../helpers/commonHelper";
import { DOMESTIC } from "../constants/flightTypes";
import { Paths } from "../constants/Paths";
import useSEO from "../hooks/useSEO";

export default function MyBookings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activeTab } = useParams();
  const { setSEO } = useSEO();

  useEffect(() => {
    setSEO(`My Bookings | Yatra Tickets`);
  }, [setSEO]);

  const currentTab = activeTab === "tourbooking" ? "packages" : "flights";

  const handleTabChange = (value) => {
    const targetPath = value === "packages" ? "tourbooking" : "flightbooking";
    navigate(`${Paths.MY_BOOKINGS}/${targetPath}`);
  };
  const columns = [
    {
      accessorKey: "sn",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full justify-start px-0"
          >
            S.No.
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("sn")}</div>,
    },
    {
      accessorKey: "BookedOn",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full px-0"
          >
            Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div>{format(row.getValue("BookedOn"), "PPP")}</div>,
    },
    {
      accessorKey: "CabinClass",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full px-0"
          >
            Class
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div className="text-center ">{isEmpty(row.getValue("CabinClass")) ? "-" : row.getValue("CabinClass")}</div>,
    },
    {
      accessorKey: "booking",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full  px-0"
          >
            Itinenary Details
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div
          dangerouslySetInnerHTML={{ __html: row.getValue("booking") }}
        ></div>
      ),
    },
    {
      accessorKey: "ValidatingAirline",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full   px-0"
          >
            Carrier
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("ValidatingAirline")}</div>,
    },
    {
      accessorKey: "PNRNo",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full  px-0"
          >
            PNR
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="text-center font-medium">{row.getValue("PNRNo")}</div>
      ),
    },
    {
      accessorKey: "BookingStatus",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full  px-0"
          >
            Payment
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="text-center font-medium">
          <div className="relative grid select-none items-center whitespace-nowrap rounded-full bg-gray-900/10 py-1.5 px-3 font-sans text-xs font-bold uppercase text-gray-900">
            {row.getValue("BookingStatus")}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "GrandTotalFare",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full justify-end px-0"
          >
            Amount
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("GrandTotalFare"));

        // Format the amount as a nepali amount
        const formatted = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "NPR",
        }).format(amount);

        return <div className="text-right font-medium">{formatted}</div>;
      },
    },
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full justify-start px-0"
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("name")}</div>,
    },
    {
      accessorKey: "TripType",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="w-full justify-start px-0"
          >
            Type
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div>{row.getValue("TripType")}</div>,
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        var status = row.getValue("BookingStatus");
        return (
          <div className="flex space-x-2">
            {status == "Booked" ?
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleProceed(row)}
                    >
                      <MdOutlinePayment className="w-4 h-4" />

                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Proceed to payment</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              : <></>}

            {(status == "Booked" || status == "Ticketed") && <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => viewBookingDetail(row)}
                  >
                    <PiClipboardText className="w-4 h-4" />

                  </Button>
                </TooltipTrigger>

                <TooltipContent>
                  <p>View detail</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>}
          </div>
        );
      },
    },
  ];

  const tourColumns = [
    {
      accessorKey: "sn",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full justify-center px-0"
        >
          S.No.
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div className="text-center">{row.getValue("sn")}</div>,
    },
    {
      accessorKey: "IsGroup",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full justify-center px-0"
        >
          Tour Type
          <ArrowUpDown className="ml-2 h-3 w-3" />
        </Button>
      ),
      //cell: ({ row }) => <div className="text-center">{row.getValue("IsGroup") ? "Group" : "Fixed"}</div>,
      cell: ({ row }) => (
        <div className="text-center font-medium">
          {/* <div className="relative grid select-none items-center whitespace-nowrap rounded-full bg-gray-900/10 py-1.5 px-3 font-sans text-xs font-bold uppercase text-gray-900">
            {row.getValue("IsGroup") ? "Group" : "Fixed"}
          </div> */}
          {row.getValue("IsGroup") ? (
            <div className="relative grid select-none items-center whitespace-nowrap rounded-full bg-[#f3e8ff] py-1.5 px-3 font-sans text-xs font-bold uppercase text-[#7e22ce]">
              Group
            </div>
          ) : (
            <div className="relative grid select-none items-center whitespace-nowrap rounded-full bg-[#e0e7ff] py-1.5 px-3 font-sans text-xs font-bold uppercase text-[#4338ca]">
              Fixed
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: "PackageTitle",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full justify-center px-0"
        >
          Package
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div className="text-center">{row.getValue("PackageTitle")}</div>,
    },
    {
      accessorKey: "DestinationName",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full justify-center px-0"
        >
          Destination
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div className="text-center">{row.getValue("DestinationName")}</div>,
    },
    {
      accessorKey: "DepartureDate",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full justify-center px-0"
        >
          Departure Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div className="text-center">{row.getValue("DepartureDate") ? format(new Date(row.getValue("DepartureDate")), "PPP") : "-"}</div>,
    },
    // {
    //   accessorKey: "PackageID",
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       className="w-full px-0 text-center"
    //     >
    //       Package ID
    //       <ArrowUpDown className="ml-2 h-4 w-4" />
    //     </Button>
    //   ),
    //   cell: ({ row }) => <div className="text-center">{row.getValue("PackageID")}</div>,
    // },
    {
      accessorKey: "FullName",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full justify-center px-0"
        >
          Contact Person
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div className="text-center">{row.getValue("FullName")}</div>,
    },
    {
      accessorKey: "BookingStatus",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full justify-center px-0"
        >
          Payment
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const status = row.getValue("BookingStatus");
        const isPaid = status === "Paid";
        return (
          <div className="text-center font-medium">
            <div className={`relative grid select-none items-center whitespace-nowrap rounded-full ${isPaid ? "bg-blue-500 text-white" : "bg-green-500 text-white"} py-1.5 px-3 font-sans text-xs font-bold uppercase`}>
              {status}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "GrandTotal",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="w-full justify-end px-0"
        >
          Total Fare
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const amount = (row.original.GrandTotal);
        const formatted = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "NPR",
        }).format(amount);
        return <div className="text-right font-medium">{formatted}</div>;
      },
    },
    {
      accessorKey: "combinedSearch",
      header: "Search",
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const status = row.getValue("BookingStatus");
        return (
          <div className="flex space-x-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => viewTourDetail(row)}
                  >
                    <PiClipboardText className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View detail</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {(status === "Pending" || status === "Booked") && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleTourProceed(row)}
                    >
                      <MdOutlinePayment className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Proceed to payment</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
  ];

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({
    combinedSearch: false,
  });
  const [rowSelection, setRowSelection] = useState({});

  const [tourSorting, setTourSorting] = useState([]);
  const [tourColumnFilters, setTourColumnFilters] = useState([]);

  const [bookingList, setBookingList] = useState([]);
  const [tourBookingList, setTourBookingList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tourLoading, setTourLoading] = useState(true);
  const handleProceed = (row) => {
    let bookingStatus = row.getValue("BookingStatus");
    switch (bookingStatus) {
      case "Booked":
        handleProceedToPayment(row.getValue("PNRNo"));
        break;
      case "Ticketed":
        viewBookingDetail(row);
        break;
      case "Void":
        break;
      case "Canceled":
        break;
      default:
        break;
    }
  };
  const viewBookingDetail = (row) => {
    let bookinfRef = row.original.BookingRefNo;
    let payload = {
      bookcode: bookinfRef,
      success: true
    };
    let detailpage = row.original.FlightType == DOMESTIC ? Paths.DOMESTIC_TICKET_PAGE : Paths.INT_TICKET_PAGE
    navigate(detailpage + '?' + new URLSearchParams(payload).toString())
  };

  // const handleViewPackageDetail = (row) => {
  //   navigate(Paths.PACKAGE_DETAIL_PAGE + '/' + row.original.PackageID + '/' + row.original.PackageTitle.replace(/\s+/g, '-').toLowerCase())
  // };

  const handleProceedToPayment = (pnr) => {
    var item = bookingList.find((d) => d.PNRNo === pnr);
    localStorage.setItem("bookinfRef", item.BookingRefNo);
    localStorage.setItem('currentFlightType', item.FlightType);
    navigate("/payment");
  };

  const viewTourDetail = (row) => {
    const { BookUID } = row.original;
    navigate(Paths.TOUR_BOOKING_DETAIL.replace(':bookUID', BookUID));
  };

  const handleTourProceed = (row) => {
    navigate(Paths.TOUR_PAYMENT, {
      state: {
        tourBookId: row.original.TourBookID,
        bookUID: row.original.BookUID,
        packageTitle: row.original.PackageTitle,
        totalFare: (row.original.GrandTotal || row.original.TotalFare || 0) * (row.original.TotalBookedSeat || 1),
        travelerCount: row.original.TotalBookedSeat || 1,
      }
    });
  };
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    dispatch(
      getMyBookings((resp) => {
        if (!isMounted) return;
        setIsLoading(false);
        setBookingList(
          (resp || []).map((res, i) => {
            return {
              ...res,
              sn: i + 1,
              booking: `
                <div>
                  ${res.Origin} to ${res.Destination}
                </div>`,
              name:
                res.ContactTitle +
                " " +
                res.ContactFirstName +
                " " +
                res.ContactLastName,
              BookingRefNo: res.BookingRefNo
            };
          })
        );
      })
    );

    setTourLoading(true);
    dispatch(
      getMyTourBookings(0, 10, "", (resp) => {
        if (!isMounted) return;
        setTourLoading(false);
        setTourBookingList(
          (resp || []).map((res, i) => {
            return {
              ...res,
              sn: i + 1,
              BookingStatus: Number(res.StatusID) === 1 ? "Booked" : (Number(res.StatusID) === 2 ? "Paid" : (res.BookingStatus || "Pending")),
              combinedSearch: `${res.PackageTitle || ""} ${res.DestinationName || ""} ${res.FullName || ""}`.toLowerCase()
            };
          })
        );
      })
    );
    return () => { isMounted = false; };
  }, [dispatch]);

  const table = useReactTable({
    data: bookingList,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const tourTable = useReactTable({
    data: tourBookingList,
    columns: tourColumns,
    onSortingChange: setTourSorting,
    onColumnFiltersChange: setTourColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting: tourSorting,
      columnFilters: tourColumnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const renderTable = (tableInstance, searchKey, dataList, loading) => {
    if (loading) return <div className="py-10 text-center">Loading...</div>;
    if (!dataList || dataList.length === 0) {
      return (
        <div className='container-md text-center p-10'>
          <FiInbox className='w-20 h-20 m-auto mb-4 text-gray-500' />
          <h2 className='text-xl font-bold mb-3'>No bookings found !</h2>
          <p>We couldn't find any bookings in this category.</p>
        </div>
      );
    }

    return (
      <div className="w-full">
        <div className="flex items-center py-2">
          <Input
            placeholder="Search bookings..."
            value={tableInstance.getColumn(searchKey)?.getFilterValue() || ""}
            onChange={(event) =>
              tableInstance.getColumn(searchKey)?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {tableInstance
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="rounded-md border bg-background">
          <Table>
            <TableHeader>
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {tableInstance.getRowModel().rows?.length ? (
                tableInstance.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() ? "selected" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={tableInstance.getAllColumns().length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-end space-x-2 py-4">
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => tableInstance.previousPage()}
              disabled={!tableInstance.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => tableInstance.nextPage()}
              disabled={!tableInstance.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <h1 className="mb-4 text-2xl font-bold">My Bookings</h1>
      <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px] mb-4">
          <TabsTrigger value="flights">Flight Bookings</TabsTrigger>
          <TabsTrigger value="packages">Tour Bookings</TabsTrigger>
        </TabsList>
        <TabsContent value="flights">
          {renderTable(table, "booking", bookingList, isLoading)}
        </TabsContent>
        <TabsContent value="packages">
          {renderTable(tourTable, "combinedSearch", tourBookingList, tourLoading)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
